// src/pages/TemplateEditPage.tsx
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Alert,
  Snackbar,
  LinearProgress,
  CircularProgress,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Add,
  Edit,
  Delete,
  ExpandMore,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import {
  getTemplate,
  updateTemplate,
  activateTemplate,
  deactivateTemplate,
  reorderTemplateElement,
  TemplateApiError,
  TemplateResponse,
  TemplatePhase,
  TemplateTask,
  TemplateAction,
} from '../services/templateApi';
import { useAuth } from '../contexts/AuthContext';
import { hasRole } from '../services/authApi';
import TemplateRenameDialog from '../components/Template/TemplateRenameDialog';

export function TemplateEditPage() {
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId: string }>();
  const { user } = useAuth();
  const [template, setTemplate] = useState<TemplateResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({ open: false, message: '', severity: 'info' });
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [editingPhaseId, setEditingPhaseId] = useState<number | null>(null);
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
  const [editingActionId, setEditingActionId] = useState<number | null>(null);
  const [isReordering, setIsReordering] = useState(false);
  const [reorderingElementId, setReorderingElementId] = useState<number | null>(null);
  // Track expanded accordions: Set of phase IDs and task IDs
  const [expandedPhases, setExpandedPhases] = useState<Set<number>>(new Set());
  const [expandedTasks, setExpandedTasks] = useState<Set<number>>(new Set());

  // Check if user is System Admin (only System Admin can edit)
  const isAdmin = user ? hasRole(user, 'System Admin') : false;

  // Load template
  const loadTemplate = useCallback(async () => {
    if (!templateId) {
      setError('Template ID is required');
      setIsLoading(false);
      return;
    }

    const id = parseInt(templateId, 10);
    if (isNaN(id) || id <= 0) {
      setError('Invalid template ID');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const templateData = await getTemplate(id);
      console.log('Loaded template data:', templateData);
      console.log('Template phases:', templateData.phases);
      console.log('Phases count:', templateData.phases?.length || 0);
      
      // Ensure phases is always an array
      if (!templateData.phases || !Array.isArray(templateData.phases)) {
        console.warn('Template phases is not an array, initializing as empty array');
        templateData.phases = [];
      }
      
      // Sort phases by displayOrder
      templateData.phases.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
      
      // Sort tasks and actions within each phase
      templateData.phases.forEach(phase => {
        if (phase.tasks && Array.isArray(phase.tasks)) {
          phase.tasks.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
          phase.tasks.forEach(task => {
            if (task.actions && Array.isArray(task.actions)) {
              task.actions.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
            } else {
              task.actions = [];
            }
          });
        } else {
          phase.tasks = [];
        }
      });
      
      setTemplate(templateData);
      setIsDirty(false);
    } catch (err) {
      const apiError = err as TemplateApiError;
      let errorMessage = 'Failed to load template';
      
      if (apiError.code === 'AUTH_REQUIRED') {
        errorMessage = 'Authentication required. Please log in.';
      } else if (apiError.code === 'NOT_FOUND') {
        errorMessage = `Template ${id} not found`;
      } else if (apiError.message) {
        errorMessage = apiError.message;
      }
      
      setError(errorMessage);
      console.error('Error loading template:', err);
    } finally {
      setIsLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    loadTemplate();
  }, [loadTemplate]);

  // Initialize expanded state: expand first phase on initial load
  useEffect(() => {
    if (template && template.phases.length > 0 && expandedPhases.size === 0) {
      setExpandedPhases(new Set([template.phases[0].templatePhaseId]));
    }
  }, [template, expandedPhases.size]);

  // Check authorization
  useEffect(() => {
    if (!isLoading && !isAdmin) {
      setError('Access denied. Only Administrators can edit templates.');
    }
  }, [isLoading, isAdmin]);

  const handleBack = () => {
    if (isDirty) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate('/admin/templates');
      }
    } else {
      navigate('/admin/templates');
    }
  };

  const handleSave = async () => {
    if (!template || !isAdmin) return;

    try {
      setIsSaving(true);
      setError(null);
      
      await updateTemplate(template.templateId, {
        name: template.name,
        description: template.description,
        phases: template.phases,
      });

      // Automatically activate (publish) the template if not already active
      if (template.status !== 'ACTIVE') {
        try {
          await activateTemplate(template.templateId);
          setTemplate({ ...template, status: 'ACTIVE' });
        } catch (activateErr) {
          // Log but don't fail - template was saved, just not activated
          console.warn('Template saved but failed to activate:', activateErr);
        }
      }

      setIsDirty(false);
      setSnackbar({
        open: true,
        message: 'Template saved and published successfully',
        severity: 'success',
      });
    } catch (err) {
      const apiError = err as TemplateApiError;
      const errorMessage = apiError.message || 'Failed to save template';
      setError(errorMessage);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
      console.error('Error saving template:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRename = async (newName: string) => {
    if (!template) return;

    try {
      await updateTemplate(template.templateId, { name: newName });
      setTemplate({ ...template, name: newName });
      setRenameDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'Template renamed successfully',
        severity: 'success',
      });
    } catch (err) {
      const apiError = err as TemplateApiError;
      setSnackbar({
        open: true,
        message: apiError.message || 'Failed to rename template',
        severity: 'error',
      });
    }
  };

  const handleUpdateTemplate = (updates: Partial<TemplateResponse>) => {
    if (!template) return;
    setTemplate({ ...template, ...updates });
    setIsDirty(true);
  };

  // Phase handlers
  const handleAddPhase = () => {
    if (!template) return;
    const newPhase: TemplatePhase = {
      templatePhaseId: Date.now(), // Temporary ID
      name: 'New Phase',
      description: null,
      displayOrder: template.phases.length + 1,
      tasks: [],
    };
    handleUpdateTemplate({
      phases: [...template.phases, newPhase],
    });
    setEditingPhaseId(newPhase.templatePhaseId);
  };

  const handleUpdatePhase = (phaseId: number, updates: Partial<TemplatePhase>) => {
    if (!template) return;
    const updatedPhases = template.phases.map(phase =>
      phase.templatePhaseId === phaseId ? { ...phase, ...updates } : phase
    );
    handleUpdateTemplate({ phases: updatedPhases });
  };

  const handleDeletePhase = (phaseId: number) => {
    if (!template) return;
    if (template.phases.length <= 1) {
      setSnackbar({
        open: true,
        message: 'Template must have at least one phase',
        severity: 'error',
      });
      return;
    }
    if (window.confirm('Are you sure you want to delete this phase?')) {
      const updatedPhases = template.phases
        .filter(p => p.templatePhaseId !== phaseId)
        .map((phase, index) => ({ ...phase, displayOrder: index + 1 }));
      handleUpdateTemplate({ phases: updatedPhases });
    }
  };

  // Task handlers
  const handleAddTask = (phaseId: number) => {
    if (!template) return;
    const phase = template.phases.find(p => p.templatePhaseId === phaseId);
    if (!phase) return;
    
    const newTask: TemplateTask = {
      templateTaskId: Date.now(), // Temporary ID
      name: 'New Task',
      durationHours: 8,
      description: null,
      displayOrder: phase.tasks.length + 1,
      actions: [],
    };
    
    const updatedPhases = template.phases.map(p =>
      p.templatePhaseId === phaseId
        ? { ...p, tasks: [...p.tasks, newTask] }
        : p
    );
    handleUpdateTemplate({ phases: updatedPhases });
    setEditingTaskId(newTask.templateTaskId);
  };

  const handleUpdateTask = (phaseId: number, taskId: number, updates: Partial<TemplateTask>) => {
    if (!template) return;
    const updatedPhases = template.phases.map(phase =>
      phase.templatePhaseId === phaseId
        ? {
            ...phase,
            tasks: phase.tasks.map(task =>
              task.templateTaskId === taskId ? { ...task, ...updates } : task
            ),
          }
        : phase
    );
    handleUpdateTemplate({ phases: updatedPhases });
  };

  const handleDeleteTask = (phaseId: number, taskId: number) => {
    if (!template) return;
    if (window.confirm('Are you sure you want to delete this task?')) {
      const updatedPhases = template.phases.map(phase =>
        phase.templatePhaseId === phaseId
          ? {
              ...phase,
              tasks: phase.tasks
                .filter(t => t.templateTaskId !== taskId)
                .map((task, index) => ({ ...task, displayOrder: index + 1 })),
            }
          : phase
      );
      handleUpdateTemplate({ phases: updatedPhases });
    }
  };

  // Action handlers
  const handleAddAction = (phaseId: number, taskId: number) => {
    if (!template) return;
    const phase = template.phases.find(p => p.templatePhaseId === phaseId);
    if (!phase) return;
    const task = phase.tasks.find(t => t.templateTaskId === taskId);
    if (!task) return;
    
    const newAction: TemplateAction = {
      templateActionId: Date.now(), // Temporary ID
      actionType: 'CREATE',
      durationHours: 8,
      description: null,
      displayOrder: task.actions.length + 1,
    };
    
    const updatedPhases = template.phases.map(p =>
      p.templatePhaseId === phaseId
        ? {
            ...p,
            tasks: p.tasks.map(t =>
              t.templateTaskId === taskId
                ? { ...t, actions: [...t.actions, newAction] }
                : t
            ),
          }
        : p
    );
    handleUpdateTemplate({ phases: updatedPhases });
    setEditingActionId(newAction.templateActionId);
  };

  const handleUpdateAction = (
    phaseId: number,
    taskId: number,
    actionId: number,
    updates: Partial<TemplateAction>
  ) => {
    if (!template) return;
    const updatedPhases = template.phases.map(phase =>
      phase.templatePhaseId === phaseId
        ? {
            ...phase,
            tasks: phase.tasks.map(task =>
              task.templateTaskId === taskId
                ? {
                    ...task,
                    actions: task.actions.map(action =>
                      action.templateActionId === actionId ? { ...action, ...updates } : action
                    ),
                  }
                : task
            ),
          }
        : phase
    );
    handleUpdateTemplate({ phases: updatedPhases });
  };

  const handleDeleteAction = (phaseId: number, taskId: number, actionId: number) => {
    if (!template) return;
    if (window.confirm('Are you sure you want to delete this action?')) {
      const updatedPhases = template.phases.map(phase =>
        phase.templatePhaseId === phaseId
          ? {
              ...phase,
              tasks: phase.tasks.map(task =>
                task.templateTaskId === taskId
                  ? {
                      ...task,
                      actions: task.actions
                        .filter(a => a.templateActionId !== actionId)
                        .map((action, index) => ({ ...action, displayOrder: index + 1 })),
                    }
                  : task
              ),
            }
          : phase
      );
      handleUpdateTemplate({ phases: updatedPhases });
    }
  };

  // Helper functions for reordering
  const getCurrentPosition = (
    elementType: 'phase' | 'task' | 'action',
    elementId: number,
    parentId?: number
  ): number => {
    if (!template) return -1;

    if (elementType === 'phase') {
      const index = template.phases.findIndex(p => p.templatePhaseId === elementId);
      return index;
    } else if (elementType === 'task') {
      const phase = template.phases.find(p => p.templatePhaseId === parentId);
      const index = phase?.tasks.findIndex(t => t.templateTaskId === elementId);
      return index ?? -1;
    } else if (elementType === 'action') {
      // Find the phase that contains the task with this action
      for (const phase of template.phases) {
        const task = phase.tasks.find(t => t.templateTaskId === parentId);
        if (task) {
          const index = task.actions.findIndex(a => a.templateActionId === elementId);
          return index ?? -1;
        }
      }
    }
    return -1;
  };

  const isFirstItem = (
    elementType: 'phase' | 'task' | 'action',
    elementId: number,
    parentId?: number
  ): boolean => {
    return getCurrentPosition(elementType, elementId, parentId) === 0;
  };

  const isLastItem = (
    elementType: 'phase' | 'task' | 'action',
    elementId: number,
    parentId?: number
  ): boolean => {
    if (!template) return false;

    const position = getCurrentPosition(elementType, elementId, parentId);
    let totalCount = 0;

    if (elementType === 'phase') {
      totalCount = template.phases.length;
    } else if (elementType === 'task') {
      const phase = template.phases.find(p => p.templatePhaseId === parentId);
      totalCount = phase?.tasks.length ?? 0;
    } else if (elementType === 'action') {
      // Find the phase that contains the task with this action
      for (const phase of template.phases) {
        const task = phase.tasks.find(t => t.templateTaskId === parentId);
        if (task) {
          totalCount = task.actions.length;
          break;
        }
      }
    }

    return position === totalCount - 1;
  };

  // Helper functions to preserve and restore UI state
  const captureUIState = () => {
    // Capture scroll position
    const scrollY = window.scrollY;
    // Capture expanded accordions
    const savedExpandedPhases = new Set(expandedPhases);
    const savedExpandedTasks = new Set(expandedTasks);
    return { scrollY, savedExpandedPhases, savedExpandedTasks };
  };

  const restoreUIState = (state: { scrollY: number; savedExpandedPhases: Set<number>; savedExpandedTasks: Set<number> }) => {
    // Restore expanded accordions
    setExpandedPhases(state.savedExpandedPhases);
    setExpandedTasks(state.savedExpandedTasks);
    
    // Restore scroll position after DOM updates
    // Use requestAnimationFrame to ensure DOM is ready, then scroll
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({
          top: state.scrollY,
          behavior: 'auto', // Instant scroll, not smooth
        });
      });
    });
  };

  // Accordion toggle handlers
  const handlePhaseToggle = (phaseId: number) => {
    setExpandedPhases(prev => {
      const newSet = new Set(prev);
      if (newSet.has(phaseId)) {
        newSet.delete(phaseId);
      } else {
        newSet.add(phaseId);
      }
      return newSet;
    });
  };

  const handleTaskToggle = (taskId: number) => {
    setExpandedTasks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  // Reorder handlers
  const handleMoveUp = async (
    elementType: 'phase' | 'task' | 'action',
    elementId: number,
    parentId?: number
  ) => {
    if (!template || isReordering) return;

    try {
      const currentPosition = getCurrentPosition(elementType, elementId, parentId);
      if (currentPosition === 0 || currentPosition === -1) {
        // Already first, cannot move up
        return;
      }

      // Capture UI state before reordering
      const uiState = captureUIState();

      const newPosition = currentPosition - 1;
      setIsReordering(true);
      setReorderingElementId(elementId);

      await reorderTemplateElement(
        template.templateId,
        elementType,
        elementId,
        newPosition,
        parentId
      );

      // Refresh template data
      await loadTemplate();

      // Restore UI state after reload
      restoreUIState(uiState);

      setSnackbar({
        open: true,
        message: 'Element moved up successfully',
        severity: 'success',
      });
    } catch (err) {
      const apiError = err as TemplateApiError;
      setSnackbar({
        open: true,
        message: `Failed to move element: ${apiError.message || 'Unknown error'}`,
        severity: 'error',
      });
    } finally {
      setIsReordering(false);
      setReorderingElementId(null);
    }
  };

  const handleMoveDown = async (
    elementType: 'phase' | 'task' | 'action',
    elementId: number,
    parentId?: number
  ) => {
    if (!template || isReordering) return;

    try {
      const currentPosition = getCurrentPosition(elementType, elementId, parentId);
      if (currentPosition === -1) {
        return;
      }

      let totalCount = 0;
      if (elementType === 'phase') {
        totalCount = template.phases.length;
      } else if (elementType === 'task') {
        const phase = template.phases.find(p => p.templatePhaseId === parentId);
        totalCount = phase?.tasks.length ?? 0;
      } else if (elementType === 'action') {
        for (const phase of template.phases) {
          const task = phase.tasks.find(t => t.templateTaskId === parentId);
          if (task) {
            totalCount = task.actions.length;
            break;
          }
        }
      }

      if (currentPosition >= totalCount - 1) {
        // Already last, cannot move down
        return;
      }

      // Capture UI state before reordering
      const uiState = captureUIState();

      const newPosition = currentPosition + 1;
      setIsReordering(true);
      setReorderingElementId(elementId);

      await reorderTemplateElement(
        template.templateId,
        elementType,
        elementId,
        newPosition,
        parentId
      );

      // Refresh template data
      await loadTemplate();

      // Restore UI state after reload
      restoreUIState(uiState);

      setSnackbar({
        open: true,
        message: 'Element moved down successfully',
        severity: 'success',
      });
    } catch (err) {
      const apiError = err as TemplateApiError;
      setSnackbar({
        open: true,
        message: `Failed to move element: ${apiError.message || 'Unknown error'}`,
        severity: 'error',
      });
    } finally {
      setIsReordering(false);
      setReorderingElementId(null);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <LinearProgress sx={{ width: '50%', mb: 2 }} />
        <Typography sx={{ color: 'white' }}>Loading template...</Typography>
      </Box>
    );
  }

  if (error && !template) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button onClick={handleBack} startIcon={<ArrowBack />}>
          Back to Templates
        </Button>
      </Box>
    );
  }

  if (!template) {
    return null;
  }

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Access denied. Only Administrators can edit templates.
        </Alert>
        <Button onClick={handleBack} startIcon={<ArrowBack />}>
          Back to Templates
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBack}
            variant="outlined"
            sx={{
              color: 'white',
              borderColor: 'white',
              '&:hover': {
                borderColor: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }
            }}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h4" sx={{ color: 'white' }}>{template.name}</Typography>
            {template.description && (
              <Typography variant="body2" sx={{ color: 'white' }}>
                {template.description}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={() => setRenameDialogOpen(true)}
            sx={{ 
              backgroundColor: '#1976d2', 
              color: 'white',
              '&:hover': {
                backgroundColor: '#1565c0',
              }
            }}
          >
            Rename
          </Button>
          {template.status === 'ACTIVE' ? (
            <Button
              variant="outlined"
              color="warning"
              onClick={async () => {
                if (!template) return;
                try {
                  await deactivateTemplate(template.templateId);
                  setTemplate({ ...template, status: 'ARCHIVED' });
                  setSnackbar({
                    open: true,
                    message: 'Template unpublished successfully',
                    severity: 'success',
                  });
                } catch (err) {
                  const apiError = err as TemplateApiError;
                  setSnackbar({
                    open: true,
                    message: apiError.message || 'Failed to unpublish template',
                    severity: 'error',
                  });
                }
              }}
            >
              Unpublish
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="success"
              onClick={async () => {
                if (!template) return;
                try {
                  await activateTemplate(template.templateId);
                  setTemplate({ ...template, status: 'ACTIVE' });
                  setSnackbar({
                    open: true,
                    message: 'Template published successfully',
                    severity: 'success',
                  });
                } catch (err) {
                  const apiError = err as TemplateApiError;
                  setSnackbar({
                    open: true,
                    message: apiError.message || 'Failed to publish template',
                    severity: 'error',
                  });
                }
              }}
            >
              Publish
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={!isDirty || isSaving}
            sx={{ 
              backgroundColor: '#1976d2', 
              color: 'white !important',
              border: 'none !important',
              '&:hover': {
                backgroundColor: '#1565c0',
              },
              '&:disabled': {
                backgroundColor: '#1976d2 !important',
                color: 'white !important',
                opacity: 0.6,
              }
            }}
          >
            {isSaving ? 'Saving...' : 'Save Template'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Template Description */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          label="Description"
          fullWidth
          multiline
          rows={2}
          value={template.description || ''}
          onChange={(e) => handleUpdateTemplate({ description: e.target.value || null })}
          placeholder="Enter template description (optional)"
        />
      </Paper>

      {/* Phases */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ color: 'white' }}>Phases ({template.phases.length})</Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddPhase}
            size="small"
            sx={{ 
              backgroundColor: '#1976d2', 
              color: 'white',
              '&:hover': {
                backgroundColor: '#1565c0',
              }
            }}
          >
            Add Phase
          </Button>
        </Box>

        {!template.phases || template.phases.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              No phases defined. Add your first phase to get started.
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={handleAddPhase}>
              Add First Phase
            </Button>
          </Paper>
        ) : (
          (template.phases || []).map((phase, _phaseIndex) => (
            <Accordion 
              key={phase.templatePhaseId} 
              expanded={expandedPhases.has(phase.templatePhaseId)}
              onChange={() => handlePhaseToggle(phase.templatePhaseId)}
              sx={{
                ...(reorderingElementId === phase.templatePhaseId && {
                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                }),
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                  {/* Order number */}
                  <Typography variant="body2" sx={{ color: 'text.secondary', mr: 1, minWidth: '24px' }}>
                    {phase.displayOrder + 1}.
                  </Typography>
                  
                  {/* Reorder buttons */}
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMoveUp('phase', phase.templatePhaseId);
                    }}
                    disabled={isFirstItem('phase', phase.templatePhaseId) || isReordering}
                    title="Move Up"
                    sx={{ mr: 0.5 }}
                  >
                    {isReordering && reorderingElementId === phase.templatePhaseId ? (
                      <CircularProgress size={16} />
                    ) : (
                      <ArrowUpward fontSize="small" />
                    )}
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMoveDown('phase', phase.templatePhaseId);
                    }}
                    disabled={isLastItem('phase', phase.templatePhaseId) || isReordering}
                    title="Move Down"
                    sx={{ mr: 1 }}
                  >
                    {isReordering && reorderingElementId === phase.templatePhaseId ? (
                      <CircularProgress size={16} />
                    ) : (
                      <ArrowDownward fontSize="small" />
                    )}
                  </IconButton>
                  
                  {/* Phase name */}
                  <Typography variant="subtitle1" sx={{ fontWeight: 500, mr: 2 }}>
                    {phase.name}
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingPhaseId(phase.templatePhaseId);
                    }}
                    title="Edit Phase"
                    color="primary"
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePhase(phase.templatePhaseId);
                    }}
                    title="Delete Phase"
                    color="error"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ pl: 2 }}>
                  {/* Phase Edit Form */}
                  {editingPhaseId === phase.templatePhaseId ? (
                    <Paper sx={{ p: 2, mb: 2 }}>
                      <TextField
                        label="Phase Name"
                        fullWidth
                        value={phase.name}
                        onChange={(e) =>
                          handleUpdatePhase(phase.templatePhaseId, { name: e.target.value })
                        }
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        label="Description"
                        fullWidth
                        multiline
                        rows={2}
                        value={phase.description || ''}
                        onChange={(e) =>
                          handleUpdatePhase(phase.templatePhaseId, {
                            description: e.target.value || null,
                          })
                        }
                        sx={{ mb: 2 }}
                      />
                      <Button
                        variant="outlined"
                        onClick={() => setEditingPhaseId(null)}
                        size="small"
                      >
                        Done
                      </Button>
                    </Paper>
                  ) : (
                    <>
                      {phase.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {phase.description}
                        </Typography>
                      )}
                    </>
                  )}

                  {/* Tasks */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2">Tasks ({phase.tasks.length})</Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Add />}
                        onClick={() => handleAddTask(phase.templatePhaseId)}
                      >
                        Add Task
                      </Button>
                    </Box>

                    {phase.tasks.map((task, _taskIndex) => (
                      <Accordion 
                        key={task.templateTaskId}
                        expanded={expandedTasks.has(task.templateTaskId)}
                        onChange={() => handleTaskToggle(task.templateTaskId)}
                        sx={{ 
                          mb: 1,
                          ...(reorderingElementId === task.templateTaskId && {
                            backgroundColor: 'rgba(25, 118, 210, 0.1)',
                          }),
                        }}
                      >
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                            {/* Order number */}
                            <Typography variant="body2" sx={{ color: 'text.secondary', mr: 1, minWidth: '24px' }}>
                              {task.displayOrder + 1}.
                            </Typography>
                            
                            {/* Reorder buttons */}
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMoveUp('task', task.templateTaskId, phase.templatePhaseId);
                              }}
                              disabled={isFirstItem('task', task.templateTaskId, phase.templatePhaseId) || isReordering}
                              title="Move Up"
                              sx={{ mr: 0.5 }}
                            >
                              {isReordering && reorderingElementId === task.templateTaskId ? (
                                <CircularProgress size={16} />
                              ) : (
                                <ArrowUpward fontSize="small" />
                              )}
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMoveDown('task', task.templateTaskId, phase.templatePhaseId);
                              }}
                              disabled={isLastItem('task', task.templateTaskId, phase.templatePhaseId) || isReordering}
                              title="Move Down"
                              sx={{ mr: 1 }}
                            >
                              {isReordering && reorderingElementId === task.templateTaskId ? (
                                <CircularProgress size={16} />
                              ) : (
                                <ArrowDownward fontSize="small" />
                              )}
                            </IconButton>
                            
                            {/* Task name */}
                            <Typography variant="body2" sx={{ fontWeight: 500, mr: 2 }}>
                              {task.name} ({task.durationHours}h)
                            </Typography>
                            <Box sx={{ flexGrow: 1 }} />
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingTaskId(task.templateTaskId);
                              }}
                              title="Edit Task"
                              color="primary"
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTask(phase.templatePhaseId, task.templateTaskId);
                              }}
                              title="Delete Task"
                              color="error"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box sx={{ pl: 2 }}>
                            {/* Task Edit Form */}
                            {editingTaskId === task.templateTaskId ? (
                              <Paper sx={{ p: 2, mb: 2 }}>
                                <TextField
                                  label="Task Name"
                                  fullWidth
                                  value={task.name}
                                  onChange={(e) =>
                                    handleUpdateTask(phase.templatePhaseId, task.templateTaskId, {
                                      name: e.target.value,
                                    })
                                  }
                                  sx={{ mb: 2 }}
                                />
                                <TextField
                                  label="Description"
                                  fullWidth
                                  multiline
                                  rows={2}
                                  value={task.description || ''}
                                  onChange={(e) =>
                                    handleUpdateTask(phase.templatePhaseId, task.templateTaskId, {
                                      description: e.target.value || null,
                                    })
                                  }
                                  sx={{ mb: 2 }}
                                />
                                <TextField
                                  label="Duration (Hours)"
                                  type="number"
                                  fullWidth
                                  value={task.durationHours}
                                  onChange={(e) =>
                                    handleUpdateTask(phase.templatePhaseId, task.templateTaskId, {
                                      durationHours: parseInt(e.target.value, 10) || 0,
                                    })
                                  }
                                  sx={{ mb: 2 }}
                                  inputProps={{ min: 0 }}
                                />
                                <Button
                                  variant="outlined"
                                  onClick={() => setEditingTaskId(null)}
                                  size="small"
                                >
                                  Done
                                </Button>
                              </Paper>
                            ) : (
                              <>
                                {task.description && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    {task.description}
                                  </Typography>
                                )}
                              </>
                            )}

                            {/* Actions */}
                            <Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="body2" fontWeight="medium">
                                  Actions ({task.actions.length})
                                </Typography>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<Add />}
                                  onClick={() => handleAddAction(phase.templatePhaseId, task.templateTaskId)}
                                >
                                  Add Action
                                </Button>
                              </Box>

                              {task.actions.map((action, _actionIndex) => (
                                <Paper 
                                  key={action.templateActionId} 
                                  sx={{ 
                                    p: 2, 
                                    mb: 1,
                                    ...(reorderingElementId === action.templateActionId && {
                                      backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                    }),
                                  }}
                                >
                                  {editingActionId === action.templateActionId ? (
                                    <Box>
                                      <FormControl fullWidth sx={{ mb: 2 }}>
                                        <InputLabel>Action Type</InputLabel>
                                        <Select
                                          value={action.actionType}
                                          label="Action Type"
                                          onChange={(e) =>
                                            handleUpdateAction(
                                              phase.templatePhaseId,
                                              task.templateTaskId,
                                              action.templateActionId,
                                              { actionType: e.target.value as TemplateAction['actionType'] }
                                            )
                                          }
                                        >
                                          <MenuItem value="CREATE">CREATE</MenuItem>
                                          <MenuItem value="REVIEW">REVIEW</MenuItem>
                                          <MenuItem value="APPROVE">APPROVE</MenuItem>
                                          <MenuItem value="SUBMIT">SUBMIT</MenuItem>
                                          <MenuItem value="NOTIFY">NOTIFY</MenuItem>
                                          <MenuItem value="OTHER">OTHER</MenuItem>
                                        </Select>
                                      </FormControl>
                                      <TextField
                                        label="Description"
                                        fullWidth
                                        multiline
                                        rows={2}
                                        value={action.description || ''}
                                        onChange={(e) =>
                                          handleUpdateAction(
                                            phase.templatePhaseId,
                                            task.templateTaskId,
                                            action.templateActionId,
                                            { description: e.target.value || null }
                                          )
                                        }
                                        sx={{ mb: 2 }}
                                      />
                                      <TextField
                                        label="Duration (Hours)"
                                        type="number"
                                        fullWidth
                                        value={action.durationHours}
                                        onChange={(e) =>
                                          handleUpdateAction(
                                            phase.templatePhaseId,
                                            task.templateTaskId,
                                            action.templateActionId,
                                            { durationHours: parseInt(e.target.value, 10) || 0 }
                                          )
                                        }
                                        sx={{ mb: 2 }}
                                        inputProps={{ min: 0 }}
                                      />
                                      <Button
                                        variant="outlined"
                                        onClick={() => setEditingActionId(null)}
                                        size="small"
                                      >
                                        Done
                                      </Button>
                                    </Box>
                                  ) : (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                        {/* Order number */}
                                        <Typography variant="body2" sx={{ color: 'text.secondary', mr: 1, minWidth: '24px' }}>
                                          {action.displayOrder + 1}.
                                        </Typography>
                                        
                                        {/* Reorder buttons */}
                                        <IconButton
                                          size="small"
                                          onClick={() => handleMoveUp('action', action.templateActionId, task.templateTaskId)}
                                          disabled={isFirstItem('action', action.templateActionId, task.templateTaskId) || isReordering}
                                          title="Move Up"
                                          sx={{ mr: 0.5 }}
                                        >
                                          {isReordering && reorderingElementId === action.templateActionId ? (
                                            <CircularProgress size={16} />
                                          ) : (
                                            <ArrowUpward fontSize="small" />
                                          )}
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          onClick={() => handleMoveDown('action', action.templateActionId, task.templateTaskId)}
                                          disabled={isLastItem('action', action.templateActionId, task.templateTaskId) || isReordering}
                                          title="Move Down"
                                          sx={{ mr: 1 }}
                                        >
                                          {isReordering && reorderingElementId === action.templateActionId ? (
                                            <CircularProgress size={16} />
                                          ) : (
                                            <ArrowDownward fontSize="small" />
                                          )}
                                        </IconButton>
                                        
                                        {/* Action details */}
                                        <Box>
                                          <Typography variant="body2" fontWeight="medium">
                                            {action.actionType}
                                            {action.description && ` - ${action.description}`}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            Duration: {action.durationHours} hours
                                          </Typography>
                                        </Box>
                                      </Box>
                                      <Box>
                                        <IconButton
                                          size="small"
                                          onClick={() => setEditingActionId(action.templateActionId)}
                                          title="Edit Action"
                                          color="primary"
                                        >
                                          <Edit fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            handleDeleteAction(
                                              phase.templatePhaseId,
                                              task.templateTaskId,
                                              action.templateActionId
                                            )
                                          }
                                          title="Delete Action"
                                          color="error"
                                        >
                                          <Delete fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    </Box>
                                  )}
                                </Paper>
                              ))}
                            </Box>
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Box>

      {/* Rename Dialog */}
      <TemplateRenameDialog
        open={renameDialogOpen}
        onClose={() => setRenameDialogOpen(false)}
        currentName={template.name}
        onRename={handleRename}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

